interface ButtonActionProps {
  feedbackSuccess: boolean;
  redirectToHelpOnline?: () => void;
  handleSubmitFeedback: () => void;
  setAmplitudeFeedbackLoading: (loading: boolean) => void;
  handleClose: () => void;
  navigate: (to: string) => void;
  activeLocation: string;
  hideTicketButton?: boolean;
  feedbackResponseData?: { id?: string };
}

interface ButtonConfigParams extends ButtonActionProps {
  feedbackComment: string;
  amplitudeFeedbackLoading: boolean;
  submitFeedbackLoading: boolean;
  isExtraSmallScreen: boolean;
  closeButtonText: string;
}

type ButtonMode =
  | 'pending-with-redirect'
  | 'pending-no-redirect'
  | 'success-with-ticket'
  | 'success-no-ticket';

const resolveMode = (
  feedbackSuccess: boolean,
  redirectToHelpOnline: (() => void) | undefined,
  hideTicketButton: boolean | undefined,
  feedbackResponseData: { id?: string } | undefined
): ButtonMode => {
  if (!feedbackSuccess && redirectToHelpOnline) return 'pending-with-redirect';
  if (!feedbackSuccess) return 'pending-no-redirect';
  if (!hideTicketButton && feedbackResponseData?.id)
    return 'success-with-ticket';
  return 'success-no-ticket';
};

export const getButtonConfig = ({
  feedbackSuccess,
  redirectToHelpOnline,
  handleSubmitFeedback,
  setAmplitudeFeedbackLoading,
  handleClose,
  navigate,
  activeLocation,
  hideTicketButton,
  feedbackResponseData,
  feedbackComment,
  amplitudeFeedbackLoading,
  submitFeedbackLoading,
  isExtraSmallScreen,
  closeButtonText,
}: ButtonConfigParams) => {
  const mode = resolveMode(
    feedbackSuccess,
    redirectToHelpOnline,
    hideTicketButton,
    feedbackResponseData
  );

  switch (mode) {
    case 'pending-with-redirect':
      return {
        mode,
        shouldDisablePrimary:
          !feedbackComment?.trim().length || amplitudeFeedbackLoading,
        shouldDisableSecondary: amplitudeFeedbackLoading,
        primaryAction: () => setAmplitudeFeedbackLoading(true),
        secondaryAction: redirectToHelpOnline ?? (() => undefined),
        primaryText: 'Submit and go to HELP Online',
        secondaryText: 'Skip and go to HELP Online',
        isLoading: amplitudeFeedbackLoading,
      };
    case 'pending-no-redirect':
      return {
        mode,
        shouldDisablePrimary: !feedbackComment?.trim().length,
        shouldDisableSecondary:
          submitFeedbackLoading || amplitudeFeedbackLoading,
        primaryAction: handleSubmitFeedback,
        secondaryAction: handleClose,
        primaryText: !isExtraSmallScreen ? 'Submit feedback' : 'Submit',
        secondaryText: 'Cancel',
        isLoading: submitFeedbackLoading || amplitudeFeedbackLoading,
      };
    case 'success-with-ticket':
      return {
        mode,
        shouldDisablePrimary: false,
        shouldDisableSecondary: false,
        primaryAction: () => {
          handleClose();
          navigate(`/tickets/${activeLocation}/${feedbackResponseData?.id}`);
        },
        secondaryAction: handleClose,
        primaryText: 'Go to feedback ticket',
        secondaryText: closeButtonText,
        isLoading: false,
      };
    case 'success-no-ticket':
      return {
        mode,
        shouldDisablePrimary: false,
        shouldDisableSecondary: false,
        primaryAction: undefined,
        secondaryAction: handleClose,
        primaryText: undefined,
        secondaryText: closeButtonText,
        isLoading: false,
      };
  }
};
